import React from 'react';
import {
  AlertStatus,
  AlertStatusLabels,
  AlertsListQuery
} from "@techgitter/contracts";
import { 
  TextField, 
  Select, 
  MenuItem, 
  FormControlLabel, 
  Checkbox,
  Button,
  Box,
  FormControl,
  InputLabel
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { DateField } from '../dropdown';

interface AlertsFilterBarProps {
  filters: AlertsListQuery;
  onFiltersChange: (filters: AlertsListQuery) => void;
  onRefresh: () => void;
}

export const AlertsFilterBar: React.FC<AlertsFilterBarProps> = ({
  filters,
  onFiltersChange,
  onRefresh
}) => {
  const getTodayString = () => {
    const now = new Date();
    const sydneyOffset = 11 * 60;
    const localOffset = now.getTimezoneOffset();
    const sydneyTime = new Date(now.getTime() + (sydneyOffset + localOffset) * 60000);
    return sydneyTime.toISOString().split('T')[0];
  };

  const handleFilterChange = (field: keyof AlertsListQuery, value: any) => {
    onFiltersChange({
      ...filters,
      [field]: value,
      cursor: undefined
    });
  };

  const handleClearFilters = () => {
    onFiltersChange({
      createdFrom: getTodayString(),
      active: true,
      pageSize: 50
    });
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      gap: 2, 
      padding: 2, 
      backgroundColor: '#f5f5f5',
      borderRadius: 1,
      flexWrap: 'wrap',
      alignItems: 'center'
    }}>
      <FormControl size="small" sx={{ minWidth: 150 }}>
        <InputLabel>Status</InputLabel>
        <Select<string>
          value={filters.status !== undefined ? String(filters.status) : ''}
          onChange={(e) => {
            const value = e.target.value as string;
            if (value === '') {
              handleFilterChange('status', undefined);
            } else {
              handleFilterChange('status', Number(value));
            }
          }}
          label="Status"
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value={String(AlertStatus.New)}>{AlertStatusLabels[AlertStatus.New]}</MenuItem>
          <MenuItem value={String(AlertStatus.Acknowledged)}>{AlertStatusLabels[AlertStatus.Acknowledged]}</MenuItem>
          <MenuItem value={String(AlertStatus.Resolved)}>{AlertStatusLabels[AlertStatus.Resolved]}</MenuItem>
          <MenuItem value={String(AlertStatus.NoActionRequired)}>{AlertStatusLabels[AlertStatus.NoActionRequired]}</MenuItem>
        </Select>
      </FormControl>

      <div style={{ minWidth: 160 }}>
        <DateField
          label="Created Date"
          value={filters.createdFrom || null}
          onChange={(iso) => handleFilterChange('createdFrom', iso ?? '')}
        />
      </div>

      <TextField
        size="small"
        label="Message ID"
        value={filters.messageId || ''}
        onChange={(e) => handleFilterChange('messageId', e.target.value)}
        placeholder="Exact match"
        sx={{ minWidth: 200 }}
      />

      <TextField
        size="small"
        label="Search"
        value={filters.q || ''}
        onChange={(e) => handleFilterChange('q', e.target.value)}
        placeholder="Summary, detail, or notes"
        sx={{ minWidth: 250 }}
      />

      <TextField
        size="small"
        label="Changed By"
        value={filters.changedBy || ''}
        onChange={(e) => handleFilterChange('changedBy', e.target.value)}
        sx={{ minWidth: 180 }}
      />

      <FormControlLabel
        control={
          <Checkbox
            checked={filters.active === true}
            onChange={(e) => handleFilterChange('active', e.target.checked ? true : undefined)}
          />
        }
        label="Active Only"
      />

      <Box sx={{ display: 'flex', gap: 1, marginLeft: 'auto' }}>
        <Button
          variant="outlined"
          size="small"
          onClick={handleClearFilters}
        >
          Clear
        </Button>
        <Button
          variant="contained"
          size="small"
          startIcon={<Refresh />}
          onClick={onRefresh}
        >
          Refresh
        </Button>
      </Box>
    </Box>
  );
};
